import { useCallback } from "react";
import { IUiContextModel } from "@contexts/UiContext";
import {
    EcommerceFavoritesFromRecentModal,
    FAVORITES_FROM_RECENT_MODAL_CONTENT_CLASS,
} from "@components/ecommerce/EcommerceFavoritesFromRecentModal/EcommerceFavoritesFromRecentModal";

export interface OpenFavoritesFromRecentOptions {
    /** Called after a successful bulk add with the guids that were accepted. */
    onAdded?: (addedGuids: string[]) => void;
    /** Wired to the empty state's "View full catalog" (the modal closes first). */
    onViewFullCatalog?: () => void;
}

/**
 * Opens the "Build from recent orders" modal in the app's global modal slot, sized for phones, and
 * restores the default modal classes on close. Shared by the home favorites section and Quick Order.
 */
export const useFavoritesFromRecentModal = (uiContext: IUiContextModel) => {
    const closeModal = useCallback(() => {
        uiContext.showModal(false);
        uiContext.setModalComponent(null);
        uiContext.resetModalClassesAndStyles();
    }, [uiContext]);

    const openFavoritesFromRecentModal = useCallback(
        (options: OpenFavoritesFromRecentOptions = {}) => {
            // setModalSetup only overrides the keys it is given, so clear any style/overlay left by a
            // previous modal (e.g. the duplicate-item modal) before applying ours.
            uiContext.resetModalClassesAndStyles();
            uiContext.setModalSetup({ modalContentClass: FAVORITES_FROM_RECENT_MODAL_CONTENT_CLASS });
            uiContext.setModalCloseOnEsc(true);
            uiContext.setModalCloseOnOverlayClick(true);
            uiContext.setModalComponent(
                <EcommerceFavoritesFromRecentModal
                    closeFn={closeModal}
                    onAdded={options.onAdded}
                    onViewFullCatalog={options.onViewFullCatalog}
                />
            );
            uiContext.showModal(true);
        },
        [uiContext, closeModal]
    );

    return { openFavoritesFromRecentModal, closeFavoritesFromRecentModal: closeModal };
};

export default useFavoritesFromRecentModal;
